import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { AUDIENCE_QUERY } from '../queries';
import LoadingSpinner from './LoadingSpinner';
import ErrorText from './ErrorText';
import { convertFiltersToBuilderFormat } from '../helpers/dataDictionary';
import {
  flattenLeaves,
  formatModelScoreRange,
  isMembershipLeaf,
  isModelLeaf,
  membershipLeafId,
} from '../helpers/expressionTree';
import SelectableStaticList from '../components/builder/SelectableStaticList';
import { capitalize } from '../utils';

function stateToString(state) {
  if (
    Object.keys(state).includes('min') &&
    Object.keys(state).includes('max')
  ) {
    return `Min: ${state.min}, Max: ${state.max}`;
  }

  if (state.length) {
    return state.map(
      (value, idx) => `${value}${idx < state.length - 1 ? ', ' : ''}`
    );
  }

  if (Object.keys(state).includes('values')) {
    return state.values.map(
      (value, idx) => `${value}${idx < state.values.length - 1 ? ' | ' : ''}`
    );
  }
}

function ResourcesList({ resourcesMap }) {
  return (
    <ul>
      {Object.entries(resourcesMap).map(([key, value], idx) => {
        // ATAD-146: `detail` is a ready-made description, e.g. a model's
        // "score 1–5".
        // Keyed by position too: the same model can appear twice with
        // different ranges.
        const items = value.map(({ name, state, detail }, itemIdx) => (
          <p className="py-1" key={`${name}-${itemIdx}`}>
            {name}
            {state && <span className="pl-1"> -- {stateToString(state)}</span>}
            {detail && <span className="pl-1"> -- {detail}</span>}
          </p>
        ));
        return (
          <li key={idx}>
            <SelectableStaticList
              label={key}
              items={items}
              initialOpen={true}
            />
          </li>
        );
      })}
    </ul>
  );
}

function VariablesList({ dictionary }) {
  function subCategoriesOrAttributes(value) {
    let nodes = [];

    Object.entries(value).forEach(([key, value], idx) => {
      if (key === 'attributes') {
        if (value.length > 0) {
          nodes.push(
            <>
              {value.map((attribute) => {
                return (
                  <p className="py-1 w-full" key={attribute.id}>
                    {attribute.name}{' '}
                    {attribute.state && (
                      <span className="pl-1">
                        {' '}
                        -- {stateToString(attribute.state)}
                      </span>
                    )}
                  </p>
                );
              })}
            </>
          );
        }
        return;
      }

      nodes.push(
        <li className="w-full" key={idx}>
          <SelectableStaticList
            label={key}
            items={subCategoriesOrAttributes(value)}
            initialOpen={true}
          />
        </li>
      );
    });

    return nodes;
  }

  return (
    <ul className="pl-8">
      {Object.entries(dictionary).map(([key, value], idx) => (
        <li className="w-full" key={idx}>
          <SelectableStaticList
            label={key}
            items={subCategoriesOrAttributes(value)}
            initialOpen={true}
          />
        </li>
      ))}
    </ul>
  );
}

function getFiltersMap(variables) {
  let map = {};

  variables.forEach(({ id, state }) => {
    const idPath = id.split('___');

    let curSelector;
    idPath.forEach((selector, idx) => {
      if (idx === idPath.length - 1) {
        curSelector.attributes.push({
          name: selector,
          id,
          state,
        });
        return;
      }

      if (!curSelector) {
        if (!map[selector]) {
          map[selector] = {
            attributes: [],
          };
        }
        curSelector = map[selector];
        return;
      }

      if (!curSelector[selector]) {
        curSelector[selector] = {
          attributes: [],
        };
      }

      curSelector = curSelector[selector];
    });
  });

  return map;
}

const RESOURCE_TYPE_NAMES = {
  zip: 'ZIP codes',
  // ATAD-146
  model: 'Model files',
};

function getMyDataResourcesMap(myDataResources) {
  let map = {};

  myDataResources.forEach((resource) => {
    const typeName =
      RESOURCE_TYPE_NAMES[resource.type] ?? capitalize(resource.type);
    if (!map[typeName]) {
      map[typeName] = [];
    }
    map[typeName].push(resource);
  });

  return map;
}

function StaticFiltersList({ heading, filtersMap, myDataResourcesMap }) {
  if (Object.keys(filtersMap).length === 0 && Object.keys(myDataResourcesMap).length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col w-full mb-4">
      {/* <p className="text-lg font-bold mb-2">{heading}</p> */}
      <div className="">
        {Object.keys(myDataResourcesMap).length > 0 && (
          <div className="pl-8">
            <ResourcesList resourcesMap={myDataResourcesMap} />
            {Object.keys(filtersMap).length > 0 && (
              <div className="flex flex-row items-center py-3">
                <div className="w-full h-0 border border-black"></div>
                <div className="ml-2 text-xs font-bold">And</div>
              </div>
            )}
          </div>
        )}
        {Object.keys(filtersMap).length > 0 && (
          <div className="">{<VariablesList dictionary={filtersMap} />}</div>
        )}
      </div>
    </div>
  );
}

export default function AsyncFiltersDisplay({ audienceName }) {
  const [formattedFilters, setFormattedFilters] = useState();
  const [formatError, setFormatError] = useState<string | null>(null);

  const { loading, error, data } = useQuery(AUDIENCE_QUERY, {
    variables: { audienceName },
  });

  useEffect(() => {
    if (data?.audience?.filters) {
      // ATAD-146: the loader throws on a filter kind it doesn't know rather
      // than silently leaving it out of the summary. Show that, not a crash.
      try {
        const builderFormattedFilters = convertFiltersToBuilderFormat(
          data.audience.filters
        );
        setFormattedFilters(builderFormattedFilters);
        setFormatError(null);
      } catch (err) {
        setFormattedFilters(undefined);
        setFormatError((err as Error).message);
      }
    }
  }, [data]);

  return (
    <div className="w-full">
      {loading && (
        <LoadingSpinner message="Getting filters..." className="mt-10" />
      )}
      {!loading && error && <ErrorText message={error.message} />}
      {!loading && !error && formatError && <ErrorText message={formatError} />}
      {!loading && !error && formattedFilters && (() => {
        // PR 2c: leaves can be variable, audience, or file. Flatten the
        // trees and split into variable leaves (for the data-dictionary
        // map) and membership leaves (rendered as a flat resources map).
        const includeLeaves = flattenLeaves(formattedFilters.expression);
        const excludeLeaves = flattenLeaves(formattedFilters.excludeExpression);

        const includeVariables = includeLeaves
          .filter((l: any) => !l.kind || l.kind === 'variable')
          .map((l: any) => l.variable);
        const excludeVariables = excludeLeaves
          .filter((l: any) => !l.kind || l.kind === 'variable')
          .map((l: any) => l.variable);

        // ATAD-146: a model's score range is the filter, so it travels with
        // the name.
        const membershipFromLeaves = (leaves: any[]) =>
          leaves.filter(isMembershipLeaf).map((l) => ({
            id: membershipLeafId(l),
            name: l.name ?? '',
            type: l.kind,
            detail: isModelLeaf(l)
              ? `score ${formatModelScoreRange(l.scoreMin, l.scoreMax)}`
              : undefined,
          }));
        const includeMembership = membershipFromLeaves(includeLeaves);
        const excludeMembership = membershipFromLeaves(excludeLeaves);

        return (
        <>
          <StaticFiltersList
            heading="Included"
            filtersMap={getFiltersMap(includeVariables)}
            myDataResourcesMap={getMyDataResourcesMap(includeMembership)}
          />
          {/* We only show Excluded if it has items, and with a heading */}
          {(Object.keys(getFiltersMap(excludeVariables)).length > 0 ||
            Object.keys(getMyDataResourcesMap(excludeMembership)).length > 0) && (
            <div className="mb-4">
              <h4 className="mb-2 font-bold text-xl">Excluded</h4>
              <StaticFiltersList
                heading="Excluded"
                filtersMap={getFiltersMap(excludeVariables)}
                myDataResourcesMap={getMyDataResourcesMap(excludeMembership)}
              />
            </div>
          )}
          <div className="flex flex-col w-full mb-8">
            <div className="">
              <p className="py-1">
                One Record Per HouseHold Selected
                <span className="font-bold bg-yellow-200"> {formattedFilters.includeOnlyOneRecordPerHousehold ? '- (Yes)' : '- (No)'} </span>
              </p>
            </div>
          </div>
        </>
        );
      })()}
    </div>
  );
}
